import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert, Loader2, Mail, KeyRound, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Gate riusabile per proteggere sezioni "sensibili" (Email SMTP, Area Pubblica).
 * Mostra una schermata di richiesta OTP fino a quando l'utente non ha verificato
 * un codice ricevuto via email. Una volta verificato, salva il token in
 * sessionStorage (chiave: `sensitive_otp_token`) e renderizza i children.
 *
 * Il token viene inviato automaticamente da api.js nell'header
 * X-Sensitive-Otp-Token per tutte le successive richieste.
 */
export default function SensitiveOtpGate({ title, description, children }) {
  const [unlocked, setUnlocked] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [code, setCode] = useState("");
  const [recipient, setRecipient] = useState("");
  const [requested, setRequested] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (sessionStorage.getItem("sensitive_otp_token")) setUnlocked(true);
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const requestOtp = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post("/agency/sensitive-otp/request");
      setRecipient(data.recipient_masked || "");
      setRequested(true);
      setSecondsLeft(data.expires_in_seconds || 900);
      toast.success(`Codice inviato a ${data.recipient_masked || "email amministratore"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio codice");
    } finally {
      setRequesting(false);
    }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) {
      toast.error("Inserisci un codice di 6 cifre");
      return;
    }
    setVerifying(true);
    try {
      const { data } = await api.post("/agency/sensitive-otp/verify", { code });
      sessionStorage.setItem("sensitive_otp_token", data.sensitive_token);
      setUnlocked(true);
      toast.success("Verifica riuscita — accesso autorizzato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Codice non valido");
    } finally {
      setVerifying(false);
    }
  };

  const lockAgain = () => {
    sessionStorage.removeItem("sensitive_otp_token");
    setUnlocked(false);
    setRequested(false);
    setCode("");
    setRecipient("");
    setSecondsLeft(0);
  };

  if (unlocked) {
    return (
      <div className="space-y-3" data-testid="otp-unlocked-wrapper">
        <div className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>Sessione protetta sbloccata · valida 30 minuti.</span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={lockAgain}
            data-testid="otp-lock-again"
            className="h-6 text-[11px] text-emerald-800 hover:bg-emerald-100"
          >
            Blocca di nuovo
          </Button>
        </div>
        {children}
      </div>
    );
  }

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <Card className="max-w-md mx-auto mt-6 border-amber-200" data-testid="otp-gate">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-amber-900">
          <ShieldAlert className="h-5 w-5 text-amber-600" />
          {title || "Verifica richiesta"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          {description ||
            "Quest'area contiene impostazioni sensibili. Per accedere, richiedi un codice di verifica all'email dell'amministratore dell'agenzia."}
        </p>
        {!requested ? (
          <Button
            onClick={requestOtp}
            disabled={requesting}
            data-testid="otp-request-btn"
            className="w-full bg-amber-600 hover:bg-amber-700 text-white"
          >
            {requesting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Mail className="h-4 w-4 mr-2" />
            )}
            Ricevi codice via email
          </Button>
        ) : (
          <>
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800">
              Codice inviato a <b>{recipient || "email amministratore"}</b>.
              {secondsLeft > 0 && (
                <span>
                  {" "}
                  Scade tra <b>{mins}:{String(secs).padStart(2, "0")}</b>.
                </span>
              )}
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">Codice OTP (6 cifre)</label>
              <Input
                data-testid="otp-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                className="text-center tracking-[0.4em] font-mono text-lg"
                maxLength={6}
                inputMode="numeric"
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={verify}
                disabled={verifying || code.length !== 6}
                data-testid="otp-verify-btn"
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {verifying ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <KeyRound className="h-4 w-4 mr-2" />
                )}
                Verifica
              </Button>
              <Button
                variant="outline"
                onClick={requestOtp}
                disabled={requesting}
                data-testid="otp-resend-btn"
                className="text-xs"
              >
                Reinvia
              </Button>
            </div>
          </>
        )}
        <p className="text-[10px] text-slate-400">
          Il codice è valido per 15 minuti. Una volta verificato, la sessione protetta resta sbloccata per 30 minuti.
        </p>
      </CardContent>
    </Card>
  );
}
